/**
 * Executive tab — Season Actuals workbook (Chris, 2026-09-03).
 *
 *   1. "Summary"             — every month: services ran (turns), max locos,
 *                              passengers, tickets sold, revenue, costs, profit;
 *                              the season total; then every ticket type sold
 *                              in the season by quantity and value.
 *   2. One sheet per month   — every RUNNING day: passengers, services ran,
 *                              tickets sold, revenue (fares + other takings),
 *                              costs, profit (green = profit, red = loss), and
 *                              how many of the day's services made a profit /
 *                              a loss; the month total; the month's tickets.
 *   3. "Profit Making Days"  — every profitable day: its profit first, then the
 *                              profitable services that ran (departure, from,
 *                              to, passengers, revenue, variable cost, profit),
 *                              summed; and at the foot what ONLY those services
 *                              add up to in passengers, revenue and cost.
 *   4. "Loss Making Days"    — the same for the loss-making days and their
 *                              loss-making services.
 *
 * exceljs is dynamically imported on use (repo convention). Downloads via
 * downloadBlob — never a popup (law 7).
 */
import type ExcelJS from 'exceljs' // types only — the library is dynamically imported on use
import { downloadBlob } from './researchShared'
import type { SaSeason, SaDay, SaTicket } from './execSeasonActuals'

const HEAD_BG = 'FF0B3D2E'
const SUB_BG = 'FFEDE7D8'
const BLOCK_BG = 'FFF7F3EA'
const MUTED = 'FF6B6B6B'
const PROFIT_BG = 'FFDDF2DD'
const PROFIT_INK = 'FF1E5E2A'
const LOSS_BG = 'FFF8D7D7'
const LOSS_INK = 'FF8C2B1F'
const MONEY = '£#,##0'
const MONEY2 = '£#,##0.00'
const INT = '#,##0'
const DOWS = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday']

const setCols = (ws: ExcelJS.Worksheet, widths: number[]) => { ws.columns = widths.map((w) => ({ width: w })) }
const titleRow = (ws: ExcelJS.Worksheet, text: string, span: number) => {
  const r = ws.addRow([text])
  ws.mergeCells(r.number, 1, r.number, span)
  r.getCell(1).font = { bold: true, size: 12, color: { argb: 'FFFFFFFF' } }
  r.getCell(1).fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: HEAD_BG } }
  r.height = 20
}
const noteRow = (ws: ExcelJS.Worksheet, text: string, span: number, height = 30) => {
  const r = ws.addRow([text])
  ws.mergeCells(r.number, 1, r.number, span)
  r.getCell(1).font = { italic: true, size: 9, color: { argb: MUTED } }
  r.getCell(1).alignment = { wrapText: true, vertical: 'top' }
  r.height = height
}
const headerRow = (ws: ExcelJS.Worksheet, cells: string[], rightFrom = 3) => {
  const r = ws.addRow(cells)
  r.eachCell((c, i) => {
    c.font = { bold: true, size: 9.5 }
    c.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: SUB_BG } }
    c.border = { bottom: { style: 'thin', color: { argb: 'FFB0A89A' } } }
    c.alignment = { wrapText: true, vertical: 'bottom', horizontal: i >= rightFrom ? 'right' : 'left' }
  })
  r.height = 28
}
const blockRow = (ws: ExcelJS.Worksheet, text: string, span: number) => {
  const r = ws.addRow([text])
  ws.mergeCells(r.number, 1, r.number, span)
  r.getCell(1).font = { bold: true, size: 10.5 }
  r.getCell(1).fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: BLOCK_BG } }
}
const fmt = (r: ExcelJS.Row, moneyCols: number[], intCols: number[], money2Cols: number[] = []) => {
  for (const c of moneyCols) r.getCell(c).numFmt = MONEY
  for (const c of intCols) r.getCell(c).numFmt = INT
  for (const c of money2Cols) r.getCell(c).numFmt = MONEY2
}
const boldTotal = (r: ExcelJS.Row) => { r.eachCell((c) => { c.font = { bold: true, size: 9.5 } }); r.getCell(1).fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: BLOCK_BG } } }
/** Profit cells: green when the figure is a profit, red when a loss. */
const paintProfit = (cell: ExcelJS.Cell, v: number | null) => {
  if (v == null) return
  cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: v >= 0 ? PROFIT_BG : LOSS_BG } }
  cell.font = { ...(cell.font ?? {}), bold: true, color: { argb: v >= 0 ? PROFIT_INK : LOSS_INK } }
}
const dash = '—'
const n = (v: number | null): number | string => (v == null ? dash : v)

const ticketBlock = (ws: ExcelJS.Worksheet, title: string, tickets: SaTicket[], span: number) => {
  blockRow(ws, title, span)
  const section = (label: string, rows: SaTicket[], qtyHead: string) => {
    if (rows.length === 0) return
    const lr = ws.addRow([label])
    ws.mergeCells(lr.number, 1, lr.number, span)
    lr.getCell(1).font = { bold: true, size: 9.5, color: { argb: MUTED } }
    headerRow(ws, ['Ticket', qtyHead, 'Sold (paid, net of refunds)', 'Revenue'], 2)
    let q = 0, sq = 0, nn = 0
    for (const t of rows) {
      const r = ws.addRow([t.item, t.qty, t.soldQty, t.net])
      fmt(r, [], [2, 3], [4])
      q += t.qty; sq += t.soldQty; nn += t.net
    }
    const tr = ws.addRow(['TOTAL', q, sq, nn])
    fmt(tr, [], [2, 3], [4])
    boldTotal(tr)
  }
  section('Passenger tickets — these ARE the passengers above', tickets.filter((t) => t.passenger), 'Quantity (passengers)')
  section('Other admission items — car parking, dogs, bicycles, memberships, platform tickets, vouchers: NOT passengers, their £ is in the takings', tickets.filter((t) => !t.passenger), 'Quantity (not passengers)')
}

export async function composeSeasonActualsWorkbook(sa: SaSeason): Promise<ExcelJS.Workbook> {
  const ExcelJSlib = (await import('exceljs')).default
  const wb = new ExcelJSlib.Workbook()
  wb.creator = 'NYMR Reports — Executive'

  // ---- 1. Summary ----------------------------------------------------------
  const sum = wb.addWorksheet('Summary')
  setCols(sum, [30, 10, 12, 10, 12, 12, 14, 14, 14, 14, 14])
  titleRow(sum, `Season Actuals — ${sa.season} daily services, as run`, 11)
  noteRow(sum, sa.notes.join('  '), 11, 160)
  blockRow(sum, 'Season by month — every month of the season, running or not', 11)
  headerRow(sum, ['Month', 'Running days', 'Services ran (turns)', 'Max locos', 'Passengers', 'Paid passengers', 'Revenue', 'Fixed', 'Variable', 'Costs', 'Profit'], 2)
  for (const m of sa.months) {
    const r = sum.addRow([m.label, m.days.length, m.days.length === 0 ? 'no standard services' : m.servicesRan, m.maxLocos ?? dash, m.pax, m.ticketsSold, m.revenue, n(m.fixedCost), n(m.variableCost), n(m.costs), n(m.profit)])
    fmt(r, [7, 8, 9, 10, 11], [2, 3, 4, 5, 6])
    paintProfit(r.getCell(11), m.profit)
  }
  const tr = sum.addRow(['SEASON', sa.runningDays, sa.servicesRan, sa.maxLocos ?? dash, sa.pax, sa.ticketsSold, sa.revenue, n(sa.fixedCost), n(sa.variableCost), n(sa.costs), n(sa.profit)])
  fmt(tr, [7, 8, 9, 10, 11], [2, 3, 4, 5, 6])
  boldTotal(tr)
  paintProfit(tr.getCell(11), sa.profit)
  sum.addRow([])
  const rr = sum.addRow(['Revenue composition (season)', '', '', '', '', 'Fares (timed)', sa.fareRevenue, 'Other takings', sa.otherTakings])
  fmt(rr, [7, 9], [])
  rr.getCell(1).font = { italic: true, size: 9, color: { argb: MUTED } }
  sum.addRow([])
  ticketBlock(sum, `Tickets sold — the whole ${sa.season} season, by ticket type`, sa.tickets, 11)
  sum.views = [{ state: 'frozen', ySplit: 2 }]

  // ---- 2. One sheet per running month (a month with no running days has no
  //         day rows to list — its fixed cost sits on the Summary) -----------
  for (const m of sa.months) {
    if (m.days.length === 0) continue
    const ws = wb.addWorksheet(m.label.slice(0, 31))
    setCols(ws, [12, 11, 10, 9, 8, 11, 11, 12, 12, 12, 12, 12, 12, 12, 10, 10])
    titleRow(ws, `${m.label} — daily services, running day by running day`, 16)
    noteRow(ws, `Services ran: ${m.servicesSource === 'wtt' ? `the ${m.timetableName ?? 'working'} timetable's passenger workings for each date's colour` : m.servicesSource === 'sales' ? 'the timed departures that sold a seat (thebigplan holds no working timetable for these dates; Locos not known)' : `the working timetable where thebigplan holds one (${m.timetableName ?? ''}), else the timed departures that sold a seat`}. Revenue = Fares (timed departures) + Other takings (walk-up and till sales with no session). Fixed = this day's share of the month's fixed cost (the line runs all year — a month with no running days keeps its fixed cost on the Summary). Variable = the day's trains: per running day, per turn, per train-mile, per passenger. Profit = Revenue − Fixed − Variable. Profitable / loss-making services = each working's fare revenue against its VARIABLE cost only. Green = profit, red = loss. Non-running days and days with no standard services are omitted.`, 16, 60)
    blockRow(ws, 'Running days', 16)
    headerRow(ws, ['Date', 'Day', 'Timetable', 'Services ran', 'Locos', 'Passengers', 'Paid passengers', 'Fares (timed)', 'Other takings', 'Revenue', 'Fixed', 'Variable', 'Costs', 'Profit', 'Profitable services', 'Loss-making services'], 4)
    let prof = 0, loss = 0
    for (const d of m.days) {
      const r = ws.addRow([d.date, DOWS[d.dow], d.colour ?? (d.servicesSource === 'sales' ? 'from sales' : ''), d.servicesRan, d.locos ?? dash, d.pax, d.ticketsSold, d.fareRevenue, d.otherTakings, d.revenue, n(d.fixedCost), n(d.variableCost), n(d.costs), n(d.profit), n(d.profitableServices), n(d.lossServices)])
      fmt(r, [8, 9, 10, 11, 12, 13, 14], [4, 5, 6, 7, 15, 16])
      paintProfit(r.getCell(14), d.profit)
      prof += d.profitableServices ?? 0; loss += d.lossServices ?? 0
    }
    const t = ws.addRow([`MONTH TOTAL (${m.days.length} running days)`, '', '', m.servicesRan, m.maxLocos ?? dash, m.pax, m.ticketsSold, m.fareRevenue, m.otherTakings, m.revenue, n(m.fixedCost), n(m.variableCost), n(m.costs), n(m.profit), m.costs == null ? dash : prof, m.costs == null ? dash : loss])
    fmt(t, [8, 9, 10, 11, 12, 13, 14], [4, 5, 6, 7, 15, 16])
    boldTotal(t)
    paintProfit(t.getCell(14), m.profit)
    ws.addRow([])
    blockRow(ws, 'Month summary', 16)
    const kv = (k: string, v: number | string | null, money = false) => { const r = ws.addRow([k, '', '', v ?? dash]); r.getCell(1).font = { bold: true, size: 9.5 }; if (money && typeof v === 'number') r.getCell(4).numFmt = MONEY; else if (typeof v === 'number') r.getCell(4).numFmt = INT; r.getCell(4).alignment = { horizontal: 'right' } }
    kv('Services ran (turns)', m.servicesRan)
    kv('Max locomotives on one day', m.maxLocos)
    kv('Passengers carried', m.pax)
    kv('Tickets sold', m.ticketsSold)
    kv('Revenue', m.revenue, true)
    kv('Fixed cost (the month\'s P&L fixed lines)', m.fixedCost, true)
    kv('Variable cost (the trains)', m.variableCost, true)
    kv('Costs', m.costs, true)
    kv('Profit', m.profit, true)
    ws.addRow([])
    ticketBlock(ws, `Tickets sold in ${m.label}, by ticket type`, m.tickets, 16)
    ws.views = [{ state: 'frozen', ySplit: 2 }]
  }

  // ---- 3 & 4. Profit-making / loss-making days ----------------------------
  const daysSheet = (name: string, title: string, pick: (d: SaDay) => boolean, pickSvc: (p: number | null) => boolean, intro: string) => {
    const ws = wb.addWorksheet(name)
    setCols(ws, [14, 12, 16, 16, 12, 13, 14, 13])
    titleRow(ws, title, 8)
    noteRow(ws, intro, 8, 56)
    const days = sa.months.flatMap((m) => m.days).filter((d) => d.profit != null && pick(d))
    if (days.length === 0) {
      noteRow(ws, sa.cost.loaded ? 'No such days in this season.' : 'No costs are loaded for this season, so days cannot be classed as profit- or loss-making.', 8)
      return
    }
    const grand = { services: 0, pax: 0, revenue: 0, varCost: 0, profit: 0, days: 0, fixed: 0, dayRevenue: 0, dayCost: 0 }
    for (const d of days) {
      grand.days += 1; grand.fixed += d.fixedCost ?? 0; grand.dayRevenue += d.revenue; grand.dayCost += d.costs ?? 0
      const head = ws.addRow([`${d.date} ${DOWS[d.dow]}`, d.colour ?? (d.servicesSource === 'sales' ? 'from sales' : ''), 'Day profit', d.profit ?? dash, 'Day revenue', d.revenue, 'Day costs', d.costs ?? dash])
      head.eachCell((c) => { c.font = { bold: true, size: 10 }; c.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: BLOCK_BG } } })
      fmt(head, [4, 6, 8], [])
      paintProfit(head.getCell(4), d.profit)
      headerRow(ws, ['Departure', 'Direction', 'From', 'To', 'Passengers', 'Revenue', 'Variable cost', 'Service profit'], 5)
      const svcs = d.services.filter((s) => pickSvc(s.profit))
      const st = { pax: 0, revenue: 0, varCost: 0, profit: 0 }
      for (const s of svcs) {
        const r = ws.addRow([s.time, s.direction, s.from, s.to, s.pax, s.revenue, s.varCost ?? dash, s.profit ?? dash])
        fmt(r, [6, 7, 8], [5])
        paintProfit(r.getCell(8), s.profit)
        st.pax += s.pax; st.revenue += s.revenue; st.varCost += s.varCost ?? 0; st.profit += s.profit ?? 0
      }
      const sr = ws.addRow([`${svcs.length} of ${d.services.length} services`, '', '', 'Sum', st.pax, st.revenue, st.varCost, st.profit])
      fmt(sr, [6, 7, 8], [5])
      boldTotal(sr)
      paintProfit(sr.getCell(8), st.profit)
      ws.addRow([])
      grand.services += svcs.length; grand.pax += st.pax; grand.revenue += st.revenue; grand.varCost += st.varCost; grand.profit += st.profit
    }
    blockRow(ws, `ONLY THESE SERVICES — ${grand.services} services across ${grand.days} days`, 8)
    const g = (k: string, v: number, money = true) => { const r = ws.addRow([k, '', '', '', '', '', '', v]); r.getCell(1).font = { bold: true, size: 9.5 }; r.getCell(8).numFmt = money ? MONEY : INT; return r }
    g('Passengers carried', grand.pax, false)
    g('Revenue', grand.revenue)
    g('Variable costs (per turn, per mile, per passenger)', grand.varCost)
    paintProfit(g('Contribution after variable costs', grand.profit).getCell(8), grand.profit)
    g(`Day-fixed costs of these ${grand.days} days (not attributed to any one service)`, grand.fixed)
    paintProfit(g('Contribution after variable AND day-fixed costs', grand.profit - grand.fixed).getCell(8), grand.profit - grand.fixed)
    ws.addRow([])
    noteRow(ws, `For comparison, these ${grand.days} whole days (every service, all takings): revenue £${Math.round(grand.dayRevenue).toLocaleString('en-GB')}, costs £${Math.round(grand.dayCost).toLocaleString('en-GB')}, profit £${Math.round(grand.dayRevenue - grand.dayCost).toLocaleString('en-GB')}.`, 8)
    ws.views = [{ state: 'frozen', ySplit: 2 }]
  }
  daysSheet('Profit Making Days', `Profit-making days — ${sa.season}`, (d) => (d.profit ?? 0) > 0, (p) => (p ?? 0) > 0,
    'Every running day whose revenue exceeded its costs (fixed per running day + variable). Under each day: the services that made a profit that day — a service’s profit is its fare revenue less its VARIABLE cost (per turn, per mile, per passenger); the day-fixed block is the day’s cost, never one train’s. The foot of the sheet adds up ONLY these profitable services, then shows the same with the day-fixed costs of those days set against them.')
  daysSheet('Loss Making Days', `Loss-making days — ${sa.season}`, (d) => (d.profit ?? 0) <= 0, (p) => (p ?? 0) <= 0,
    'Every running day whose costs (fixed per running day + variable) met or exceeded its revenue. Under each day: the services that made a loss that day — a service’s loss is its fare revenue less its VARIABLE cost (per turn, per mile, per passenger); the day-fixed block is the day’s cost, never one train’s. The foot of the sheet adds up ONLY these loss-making services, then shows the same with the day-fixed costs of those days set against them.')

  return wb
}

export async function downloadSeasonActualsXlsx(sa: SaSeason): Promise<void> {
  const wb = await composeSeasonActualsWorkbook(sa)
  const buf = await wb.xlsx.writeBuffer()
  const stamp = new Date().toISOString().slice(0, 10)
  downloadBlob(
    new Blob([buf], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' }),
    `season-actuals-${sa.season.replace('/', '-')}-${stamp}.xlsx`,
  )
}
